import { createContext, useContext, useEffect, useState } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { translations } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Intro from "@/components/Intro";
import MobileTabBar from "@/components/MobileTabBar";
import HomePage from "@/pages/HomePage";
import ChatPage from "@/pages/ChatPage";
import CharactersPage from "@/pages/CharactersPage";
import AndroidPage from "@/pages/AndroidPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AuthCallback from "@/components/AuthCallback";
import { Toaster } from "@/components/ui/sonner";

const LangContext = createContext(null);
export const useLang = () => useContext(LangContext);

const PAGE_ORDER = ["/", "/chat", "/characters", "/android", "/settings"];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollReset() {
    const { pathname } = useLocation();
    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        else window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function SwipeNav() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    useEffect(() => {
        let x0 = 0;
        let y0 = 0;
        const onStart = (e) => {
            const t = e.touches && e.touches[0];
            if (!t) return;
            x0 = t.clientX;
            y0 = t.clientY;
        };
        const onEnd = (e) => {
            const t = e.changedTouches && e.changedTouches[0];
            if (!t) return;
            const dx = t.clientX - x0;
            const dy = t.clientY - y0;
            if (Math.abs(dx) > 80 && Math.abs(dy) < 60) {
                const i = PAGE_ORDER.indexOf(pathname);
                if (i === -1) return;
                const next = dx < 0 ? PAGE_ORDER[i + 1] : PAGE_ORDER[i - 1];
                if (next) navigate(next);
            }
        };
        window.addEventListener("touchstart", onStart, { passive: true });
        window.addEventListener("touchend", onEnd, { passive: true });
        return () => {
            window.removeEventListener("touchstart", onStart);
            window.removeEventListener("touchend", onEnd);
        };
    }, [pathname, navigate]);
    return null;
}

function NetworkBanner() {
    const { t } = useLang();
    const [offline, setOffline] = useState(!navigator.onLine);
    const [restored, setRestored] = useState(false);
    useEffect(() => {
        const on = () => {
            setOffline(false);
            setRestored(true);
            setTimeout(() => setRestored(false), 3200);
        };
        const off = () => setOffline(true);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);
    if (!offline && !restored) return null;
    return (
        <div
            data-testid="network-banner"
            className={`fixed top-16 left-1/2 -translate-x-1/2 z-[60] font-mono2 text-[10px] tracking-[0.25em] px-5 py-2.5 rounded-full border backdrop-blur-lg transition-colors ${
                offline
                    ? "border-[#ff4bd8]/60 bg-[#ff4bd8]/12 text-[#ff4bd8]"
                    : "border-[#d7ff35]/60 bg-[#d7ff35]/12 text-[#d7ff35]"
            }`}
        >
            {offline ? t.net.offline : t.net.online}
        </div>
    );
}

function AppRoutes() {
    const location = useLocation();
    // Check URL fragment (not query params) for session_id — read from useLocation().hash (reactive)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/characters" element={<CharactersPage />} />
            <Route path="/android" element={<AndroidPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<HomePage />} />
        </Routes>
    );
}

function App() {
    const [lang, setLang] = useState("ua");
    const [theme, setTheme] = useState(() => localStorage.getItem("neon_theme") || "dark");
    const [persona, setPersona] = useState(() => localStorage.getItem("neon_persona") || "neon");
    const [speak, setSpeak] = useState(() => localStorage.getItem("neon_speak") === "1");
    const [voicePref, setVoicePref] = useState(() => localStorage.getItem("neon_voice") || "neutral");
    const [googleId, setGoogleId] = useState(() => localStorage.getItem("neon_google_id") || null);
    const [logoChar, setLogoChar] = useState(() => localStorage.getItem("neon_logo") || "david");
    const [userAvatar, setUserAvatar] = useState(() => localStorage.getItem("neon_user_avatar") || null);
    const [introDone, setIntroDone] = useState(() => localStorage.getItem("neon_intro_seen") === "1");
    const [user, setUser] = useState(null);

    const applyRemoteState = (state) => {
        const s = state?.settings || {};
        if (s.lang) setLang(s.lang);
        if (s.theme) setTheme(s.theme);
        if (s.persona) setPersona(s.persona);
        if (s.speak !== undefined) setSpeak(s.speak);
        if (s.voicePref) setVoicePref(s.voicePref);
        if (s.logoChar) setLogoChar(s.logoChar);
        if (s.userAvatar) {
            setUserAvatar(s.userAvatar);
            localStorage.setItem("neon_user_avatar", s.userAvatar);
        }
        // Cross-device chat history: pull stored session into this device and reload
        if (state?.session_id && state.session_id !== localStorage.getItem("neon_session")) {
            localStorage.setItem("neon_session", state.session_id);
            if (window.location.hash?.includes("session_id=")) {
                window.history.replaceState(null, "", window.location.pathname);
            }
            window.location.reload();
            return true;
        }
        return false;
    };

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) return;
        (async () => {
            try {
                const res = await fetch(`${API}/auth/me`, { credentials: "include" });
                if (!res.ok) return;
                const u = await res.json();
                setUser(u);
                const stateRes = await fetch(`${API}/user/state`, { credentials: "include" });
                if (stateRes.ok) applyRemoteState(await stateRes.json());
            } catch {
                /* offline — stay local */
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced push of settings + chat session to the account (cross-device sync)
    useEffect(() => {
        if (!user) return;
        const timer = setTimeout(() => {
            fetch(`${API}/user/state`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    settings: { lang, theme, persona, speak, voicePref, logoChar, userAvatar },
                    session_id: localStorage.getItem("neon_session"),
                }),
            }).catch(() => {});
        }, 800);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, theme, persona, speak, voicePref, logoChar, userAvatar, user]);

    const logout = async () => {
        try {
            await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
        } catch {
            /* offline */
        }
        setUser(null);
    };

    useEffect(() => {
        document.documentElement.dataset.theme = theme;
        localStorage.setItem("neon_theme", theme);
    }, [theme]);

    useEffect(() => {
        localStorage.setItem("neon_persona", persona);
    }, [persona]);

    useEffect(() => {
        localStorage.setItem("neon_speak", speak ? "1" : "0");
    }, [speak]);

    useEffect(() => {
        localStorage.setItem("neon_voice", voicePref);
    }, [voicePref]);

    useEffect(() => {
        localStorage.setItem("neon_logo", logoChar);
        const link = document.querySelector("link[rel~='icon']");
        if (link) link.href = logoChar === "lucy" ? "/favicon-lucy.png" : "/favicon-david.png";
    }, [logoChar]);

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
        window.__lenis = lenis;
        let raf;
        const loop = (time) => {
            lenis.raf(time);
            raf = requestAnimationFrame(loop);
        };
        raf = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(raf);
            lenis.destroy();
        };
    }, []);

    const t = translations[lang];

    return (
        <LangContext.Provider
            value={{ lang, setLang, t, theme, setTheme, persona, setPersona, speak, setSpeak, voicePref, setVoicePref, logoChar, setLogoChar, userAvatar, setUserAvatar, googleId, setGoogleId, user, setUser, logout, applyRemoteState }}
        >
            <BrowserRouter>
                <div className="min-h-screen bg-[#080a12] text-slate-100 antialiased">
                    <div className="noise" />
                    <div className="scanlines" />
                    {!introDone && !window.location.pathname.startsWith("/admin") && <Intro onDone={() => setIntroDone(true)} />}
                    <Navbar />
                    <NetworkBanner />
                    <ScrollReset />
                    <SwipeNav />
                    <main>
                        <AppRoutes />
                    </main>
                    <Footer />
                    <div className="h-[76px] md:hidden" />
                    <MobileTabBar />
                    <Toaster theme="dark" position="bottom-right" />
                </div>
            </BrowserRouter>
        </LangContext.Provider>
    );
}

export default App;
